import { z } from 'zod';
import { screenNameSchema } from './auth.js';
import {
  BUDDY_APPROVAL_MODES,
  BUDDY_REQUEST_STATUSES,
  type BuddyApprovalMode,
  type BuddyRequestStatus,
} from './constants.js';

/**
 * Buddies connect two kids so they have a persistent roster. A connection
 * between kids in different families needs parental consent; the rules live in
 * the server (the authority). These schemas are the client/server contract.
 */

/** A kid asks to add a buddy, identified by their family code + screen name. */
export const createBuddyRequestSchema = z.object({
  familyCode: z.string().trim().min(3).max(32),
  screenName: screenNameSchema,
});
export type CreateBuddyRequestInput = z.infer<typeof createBuddyRequestSchema>;

/** A family's stance on incoming buddy requests (parent-controlled). */
export const buddyApprovalModeSchema = z.enum(BUDDY_APPROVAL_MODES);

/** One entry on a kid's roster (the OTHER kid in the friendship). */
export interface BuddyView {
  /** The buddy's kid id. */
  kidId: string;
  screenName: string;
  online: boolean;
  since: string;
  /** Whether a 1:1 buddy chat can be opened right now (both kids' parents
   *  have turned buddy chat on). Doesn't reveal which side is off. */
  dmAvailable: boolean;
}

/** A buddy request as a kid sees their own outgoing ones. */
export interface KidBuddyRequestView {
  id: string;
  screenName: string;
  status: BuddyRequestStatus;
  createdAt: string;
}

/** A buddy request as the deciding parent sees it. */
export interface ParentBuddyRequestView {
  id: string;
  status: BuddyRequestStatus;
  /** Which side of the request this parent's family is on. */
  side: 'requester' | 'target';
  /** True when this parent can approve/decline it right now. */
  actionable: boolean;
  /** This family's kid involved in the request. */
  ourKid: { id: string; screenName: string };
  /** The other kid's screen name (no other-family PII is exposed). */
  otherScreenName: string;
  createdAt: string;
}

export type { BuddyApprovalMode, BuddyRequestStatus };
